import { useEffect, useState } from "react";
import { useAuth } from "../../app/providers/AuthProvider";
import { apiClient } from "../../lib/api-client";
import type { AuthProviderName, Profile } from "../../types/auth";
import { UserProfile } from "./UserProfile";

type ProfileState =
  | { status: "loading" }
  | { status: "loaded"; profile: Profile }
  | { status: "error" };

const providerNames: Record<AuthProviderName, string> = {
  google: "Google",
  vk: "VK ID",
  email: "Электронная почта",
  dev: "Тестовый вход",
};

export function ProfilePage() {
  const { logout } = useAuth();
  const [state, setState] = useState<ProfileState>({ status: "loading" });
  const [requestID, setRequestID] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    apiClient
      .get<{ data: Profile }>("/auth/me")
      .then(({ data }) => {
        if (!cancelled) setState({ status: "loaded", profile: data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [requestID]);

  if (state.status === "loading") {
    return (
      <div
        role="status"
        className="flex flex-1 items-center justify-center"
        aria-label="Загрузка профиля"
      >
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-action border-t-transparent" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div
        role="alert"
        className="flex flex-1 flex-col items-center justify-center px-6 text-center"
      >
        <h1 className="text-2xl font-extrabold tracking-[-0.04em] text-content">
          Не удалось загрузить профиль
        </h1>
        <p className="mt-2 text-sm text-muted">
          Проверьте подключение и попробуйте снова.
        </p>
        <button
          type="button"
          onClick={() => setRequestID((id) => id + 1)}
          className="mt-6 min-h-11 cursor-pointer rounded-full bg-content px-7 text-sm font-semibold text-white"
        >
          Повторить
        </button>
      </div>
    );
  }

  const { profile } = state;

  return (
    <div className="flex flex-1 flex-col px-5 pb-8 pt-7 sm:px-8 lg:px-10 lg:py-10 xl:px-14">
      <header>
        <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-action">
          Аккаунт
        </p>
        <h1 className="text-[32px] font-extrabold leading-none tracking-[-0.055em] text-content lg:text-[46px]">
          Профиль
        </h1>
      </header>

      <div className="mt-7 grid max-w-4xl gap-5 lg:grid-cols-2">
        <section
          aria-labelledby="profile-account-title"
          className="rounded-[24px] bg-white p-5 shadow-[0_14px_36px_rgba(35,33,25,0.06)] sm:p-6"
        >
          <h2 id="profile-account-title" className="text-lg font-extrabold text-content">
            Данные аккаунта
          </h2>
          <div className="mt-5">
            <UserProfile user={profile} showName />
          </div>
          <dl className="mt-6 space-y-4">
            <div>
              <dt className="text-xs font-bold uppercase tracking-[0.12em] text-muted">
                Email
              </dt>
              <dd className="mt-1 break-words text-sm font-semibold text-content">
                {profile.email || "Не указан"}
              </dd>
            </div>
          </dl>
        </section>

        <section
          aria-labelledby="profile-providers-title"
          className="rounded-[24px] bg-white p-5 shadow-[0_14px_36px_rgba(35,33,25,0.06)] sm:p-6"
        >
          <h2 id="profile-providers-title" className="text-lg font-extrabold text-content">
            Способы входа
          </h2>
          <ul className="mt-5 space-y-3">
            {profile.providers.map((provider) => (
              <li
                key={provider}
                className="flex min-h-14 items-center justify-between gap-4 rounded-2xl bg-surface-subtle px-4 py-3"
              >
                <span className="font-semibold text-content">
                  {providerNames[provider]}
                </span>
                <span className="shrink-0 rounded-full bg-action-soft px-3 py-1 text-xs font-bold text-action">
                  Подключён
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <button
        type="button"
        disabled={loggingOut}
        onClick={() => {
          setLoggingOut(true);
          void logout();
        }}
        className="mt-5 min-h-11 w-full max-w-4xl cursor-pointer rounded-2xl bg-content px-5 text-sm font-bold text-white disabled:cursor-wait disabled:opacity-60"
      >
        {loggingOut ? "Выходим…" : "Выйти из аккаунта"}
      </button>
    </div>
  );
}
